import { Button } from '@1trading/react/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@1trading/react/components/ui/form';
import { Loading } from '@1trading/react/components/ui/loading';
import { RadioGroup, RadioGroupItem, RadioGroupItemCard } from '@1trading/react/components/ui/radio-group';
import { Textarea } from '@1trading/react/components/ui/textarea';
import { useToast } from '@1trading/react/hooks/use-toast';
import { standardSchemaResolver } from '@hookform/resolvers/standard-schema';
import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import LoadingCard from '@/components/ui/loading-card';
import { PageCard, PageCardContent, PageCardFooter } from '@/components/ui/page-card';
import { PageHeading, PageHeadingBackButton, PageHeadingTitle } from '@/components/ui/page-heading';
import { BINARY_ANSWERS } from '@/constants/answers';
import { useDetailsFlowControl } from '@/features/institution/hooks/useDetailsFlowControl';
import type { InstitutionRegulations, InstitutionRegulationsOrDefaults } from '@/features/institution/regulatory-information/schema';
import { institutionRegulationsSchema } from '@/features/institution/regulatory-information/schema';
import { INSTITUTION_DETAILS_TYPE } from '@/features/institution/utils';
import { useProfileInstitutionDetailsAnswers, useProfileInstitutionDetailsMutation } from '@/hooks/queries/use-profile-institution-details';

const InstitutionRegulatoryInformationFormForm = ({ defaultValues }: { defaultValues: InstitutionRegulationsOrDefaults }) => {
  const form = useForm<InstitutionRegulations>({
    defaultValues: {
      ...defaultValues,
      licenseOrAuthorisationRegistrationDetails: defaultValues.licenseOrAuthorisationRegistrationDetails ?? '',
    },
    resolver: standardSchemaResolver(institutionRegulationsSchema),
  });
  const { isPending, mutate } = useProfileInstitutionDetailsMutation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();

  const onSubmit = (data: InstitutionRegulations) => {
    mutate(
      { regulatoryInformation: data },
      {
        onError: () => {
          toast({
            description: t('fundamentals.defaultError.message'),
            variant: 'destructive',
          });
        },
        onSuccess: () => {
          navigate('/institution/onboarding/authorisation', { replace: true });
        },
      },
    );
  };

  return (
    <Form {...form}>
      <form name="institution-regulatory-information" onSubmit={form.handleSubmit(onSubmit)}>
        <PageCardContent className="pt-8 space-y-6">
          <FormField
            name="hasRegulatorySupervisionOrOversight"
            control={form.control}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('institutionDetails.regulatoryInformation.hasRegulatorySupervisionOrOversight')}</FormLabel>
                <FormControl>
                  <RadioGroup
                    defaultValue={field.value}
                    variant="cards"
                    aria-label={t('institutionDetails.regulatoryInformation.hasRegulatorySupervisionOrOversight')}
                    onValueChange={field.onChange}
                  >
                    {BINARY_ANSWERS.map((answer) => (
                      <FormItem key={answer}>
                        <RadioGroupItemCard variant="outline">
                          <FormControl>
                            <RadioGroupItem value={answer} />
                          </FormControl>
                          <FormLabel contained>{t(`fundamentals.${answer.toLowerCase()}`)}</FormLabel>
                        </RadioGroupItemCard>
                      </FormItem>
                    ))}
                  </RadioGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="licenseOrAuthorisationRegistrationDetails"
            render={({ field }) => (
              <FormItem className="col-span-2">
                <FormLabel>{t('institutionDetails.regulatoryInformation.licenseOrAuthorisationRegistrationDetails')}*</FormLabel>
                <FormControl>
                  <Textarea {...field} placeholder={t('institutionDetails.regulatoryInformation.placeholders.licenseOrAuthorisationRegistrationDetails')} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </PageCardContent>
        <PageCardFooter>
          <Button className="relative w-full" size="lg" type="submit" variant="primary" disabled={isPending}>
            <span className={isPending ? 'invisible' : ''}>{t('buttons.next')}</span>
            {isPending ? <Loading variant="inverted" position="overlay" size="sm" /> : null}
          </Button>
        </PageCardFooter>
      </form>
    </Form>
  );
};
InstitutionRegulatoryInformationFormForm.displayName = 'InstitutionRegulatoryInformationFormForm';

const InstitutionRegulatoryInformationForm = () => {
  const { isLoading: isLoadingInstitutionDetailsAnswers, data: institutionDetailsAnswers } = useProfileInstitutionDetailsAnswers();
  const { isLoading: isLoadingInstitutionDetailsFlow } = useDetailsFlowControl(INSTITUTION_DETAILS_TYPE.ONBOARDING, 'regulatoryInformation');
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isLoading = isLoadingInstitutionDetailsAnswers || isLoadingInstitutionDetailsFlow;

  const handlePrevStep = useCallback(() => {
    navigate('/institution/onboarding/company', { replace: true });
  }, [navigate]);

  if (isLoading) return <LoadingCard />;

  const defaultValues = institutionDetailsAnswers?.regulatoryInformation ?? {};

  return (
    <section className="relative">
      <PageHeading>
        <PageHeadingTitle>{t('institutionDetails.regulatoryInformation.title')}</PageHeadingTitle>
      </PageHeading>
      <PageHeadingBackButton floatingInPage="left" fixedInLayout onClick={handlePrevStep} />
      <PageCard size="lg" bordered fixedFooter>
        <InstitutionRegulatoryInformationFormForm defaultValues={defaultValues} />
      </PageCard>
    </section>
  );
};
InstitutionRegulatoryInformationForm.displayName = 'InstitutionRegulatoryInformationForm';

export default InstitutionRegulatoryInformationForm;
